import { errorIsStatus, useClientPortalAPI } from "@certn/api";
import { rem } from "@certn/cascadia";
import {
  AvatarWithName,
  ColumnDefinition,
  DataTable,
  Illustration,
  Pagination,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { formatDate, formatTime, parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";

import { ActivityTypeFilter } from "./components/ActivityTypeFilter";
import { ActorFilter } from "./components/ActorFilter";
import { FilterBar } from "../../components/filters/FilterBar";
import { OrderTimeframeFilter } from "../../components/order-timeframe-filter/OrderTimeframeFilter";
import { useQueryParams } from "../../hooks/useQueryParams";
import { queryParamsToApiParams } from "../utils";

interface Props {
  caseId: string;
}

function ActivityLogEmptyState() {
  const { t } = useTranslation();
  return (
    <>
      <Illustration name="no-results" />
      <Text style="global/body-secondary" align="center" color="text-weak">
        {t("No results found.")}
      </Text>
    </>
  );
}

export function ActivityLogTable(props: Props) {
  const { t } = useTranslation();
  const { params, setParam } = useQueryParams();
  const {
    data: activities,
    error,
    isLoading,
  } = useClientPortalAPI(
    `/api/internal/client-portal/activity-logs/case/${props.caseId}/`,
    queryParamsToApiParams(params)
  );

  const { clientPortalActivityLogFilteringAndSorting } = useFlags();

  const rows =
    activities?.results?.map((activity, index) => {
      return { ...activity, id: index.toString() };
    }) ?? [];

  const columns: ColumnDefinition<(typeof rows)[number]>[] = [
    {
      key: "timestamp",
      name: t("Date"),
      isRowHeader: true,
      allowsSorting: clientPortalActivityLogFilteringAndSorting,
      width: "15%",
      render(item) {
        return (
          <div className={styles.listItemCell}>
            <Text style="form/body-secondary">
              {formatDate(item.timestamp)}
            </Text>
          </div>
        );
      },
    },
    {
      key: "time",
      name: t("Time"),
      width: "15%",
      isRowHeader: true,
      render(item) {
        return (
          <div>
            <Text style="global/info-label" color="text-weak">
              {formatTime(item.timestamp)}
            </Text>
          </div>
        );
      },
    },
    {
      key: "activity_type",
      name: t("Activity Type"),
      width: "15%",

      render(item) {
        return (
          <Text style="form/body-secondary">
            {/* Make this into an enum once we know what the backend is sending us */}
            {item.activity_type.label as string}
          </Text>
        );
      },
    },
    {
      key: "activity_description",
      name: t("Activity Description"),
      width: "30%",
      render(item) {
        return (
          <Text style="form/body-secondary">{item.activity_description}</Text>
        );
      },
    },
    {
      key: "actor",
      name: t("By"),
      width: "25%",
      render(item) {
        return <AvatarWithName name={parseName(item.actor.auth0_full_name)} />;
      },
    },
  ];

  return (
    <>
      <div className={styles.container}>
        {error && !errorIsStatus(error, 404) && (
          <SectionMessage
            type="danger"
            icon="error"
            sectionTitle={t("Unable to retrieve users")}
          >
            {String(error.message)}
          </SectionMessage>
        )}
        <div className={styles.titleBar}>
          <Text style="global/title-small">{t("Activity Log")}</Text>
        </div>

        {clientPortalActivityLogFilteringAndSorting && (
          <FilterBar testID="activity-log">
            <ActivityTypeFilter caseId={props.caseId} />
            <OrderTimeframeFilter param="timestamp_after" />
            <ActorFilter caseId={props.caseId} />
          </FilterBar>
        )}
        <DataTable
          isLoading={isLoading}
          testId="activity-log-data-table"
          columns={columns}
          rows={rows}
          onSortChange={(sortDescriptor) =>
            setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
          }
          sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
          aria-label="activity-log-table"
          renderEmptyState={() => <ActivityLogEmptyState />}
        />
      </div>
      <div className={styles.paginator}>
        <Pagination
          setPage={(page) => setParam("page", page.toString())}
          setPageCount={(pageCount) =>
            setParam("page_size", pageCount.toString())
          }
          total={activities?.count ? activities.count : 0}
          page={params.page ? Number(params.page) : 1}
          pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
        />
      </div>
    </>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: rem("space-6"),
    paddingRight: rem("space-5"),
    paddingLeft: rem("space-5"),
    paddingTop: rem("space-6"),
    paddingBottom: rem("space-6"),
    overflow: "scroll",
  }),
  paginator: css({
    marginRight: rem("space-4"),
    marginLeft: rem("space-4"),
    marginBottom: rem("space-6"),
  }),
  listItemCell: css({
    display: "flex",
    flexDirection: "column",
  }),
  titleBar: css({
    //paddingLeft: rem("space-2")
    paddingTop: rem("space-6"),
  }),
  byContainer: css({
    display: "flex",
    alignItems: "center",
    gap: rem("space-4"),
  }),
};
